// Sichtbarkeitsregeln – portiert aus frontend/wartungen/kategorie-einstellungen.js

import type { BuilderField, FieldOption, VisibilityRule } from "./fieldModel";
import { semanticSortOrderByKey } from "./fieldModel";

export const BEHAVIOR_RULE_OPERATORS = [
  "eq",
  "neq",
  "in",
  "notIn",
  "gt",
  "gte",
  "lt",
  "lte",
  "contains",
  "containsAny",
  "containsAll",
] as const;

export type BehaviorRuleOperator = (typeof BEHAVIOR_RULE_OPERATORS)[number];

export const BEHAVIOR_OPERATOR_LABELS: Record<string, string> = {
  eq: "ist gleich",
  neq: "ist ungleich",
  in: "ist einer von",
  notIn: "ist keiner von",
  gt: "größer als",
  gte: "größer-gleich",
  lt: "kleiner als",
  lte: "kleiner-gleich",
  contains: "enthält Auswahl",
  containsAny: "enthält mind. einen Wert aus",
  containsAll: "enthält alle Werte aus",
};

export const BEHAVIOR_SOURCE_SCOPES = ["masterdata", "inspection"] as const;
export type BehaviorSourceScope = (typeof BEHAVIOR_SOURCE_SCOPES)[number];

export const BEHAVIOR_SOURCE_SCOPE_LABELS: Record<string, string> = {
  masterdata: "Stammdaten",
  inspection: "Wartung",
};

const OPERATOR_ALIASES: Record<string, BehaviorRuleOperator> = {
  equals: "eq",
  not_equals: "neq",
  is_checked: "eq",
  is_not_checked: "neq",
};

export interface SourceFieldMeta {
  scope: BehaviorSourceScope;
  key: string;
  label: string;
  type: string;
  valueOptions: { value: string; label: string }[];
}

function fieldOptions(field: BuilderField): FieldOption[] {
  const raw = field.options_override_json?.options;
  return Array.isArray(raw) ? (raw as FieldOption[]) : [];
}

function buildFieldMeta(field: BuilderField): SourceFieldMeta | null {
  const key = String(field.field_key || "").trim();
  if (!key) return null;
  const scope: BehaviorSourceScope = field.scope === "masterdata" ? "masterdata" : "inspection";
  const valueOptions = fieldOptions(field)
    .filter((row) => row.active !== false)
    .map((row) => ({
      value: String(row.value || "").trim(),
      label: String(row.label || row.value || "").trim(),
    }))
    .filter((row) => row.value);
  return {
    scope,
    key,
    label: String(field.label || key).trim(),
    type: field.type,
    valueOptions,
  };
}

/** Quellfelder für Sichtbarkeitsregeln (Wartungsfelder nur vor dem Zielfeld). */
export function getAvailableSourceFields(
  allFields: BuilderField[],
  targetField: BuilderField,
): SourceFieldMeta[] {
  const sortOrder = semanticSortOrderByKey(allFields);
  const targetOrder = sortOrder.get(targetField.field_key) ?? Number.MAX_SAFE_INTEGER;

  const inspectionFields = allFields
    .filter((f) => f.scope === "inspection" && f.uid !== targetField.uid)
    .filter((f) => (sortOrder.get(f.field_key) ?? -1) < targetOrder)
    .map(buildFieldMeta)
    .filter((f): f is SourceFieldMeta => f != null);

  const seen = new Set<string>();
  const masterdataFields = allFields
    .filter((f) => f.scope === "masterdata")
    .map(buildFieldMeta)
    .filter((f): f is SourceFieldMeta => {
      if (!f || seen.has(f.key)) return false;
      seen.add(f.key);
      return true;
    });

  return [...inspectionFields, ...masterdataFields];
}

export function normalizeOperator(raw: unknown): BehaviorRuleOperator {
  const op = String(raw || "").trim();
  if ((BEHAVIOR_RULE_OPERATORS as readonly string[]).includes(op)) return op as BehaviorRuleOperator;
  return OPERATOR_ALIASES[op] || "eq";
}

export function normalizeSourceScope(raw: unknown): BehaviorSourceScope {
  const scope = String(raw || "").trim().toLowerCase();
  return scope === "masterdata" ? "masterdata" : "inspection";
}

export function normalizeVisibilityRule(raw: VisibilityRule): VisibilityRule {
  return {
    rule_type: "visibleIf",
    source_scope: normalizeSourceScope(raw.source_scope),
    source_field_key: String(raw.source_field_key || "").trim(),
    operator: normalizeOperator(raw.operator),
    value: raw.value ?? "",
  };
}

const OPTION_FIELD_TYPES = new Set(["select", "multiselect", "choice", "article_ref_list"]);

export function valueOptionsForRule(
  sourceField: SourceFieldMeta | null,
  currentValue: unknown,
): { value: string; label: string }[] {
  const base = sourceField?.valueOptions ?? [];
  const extras: { value: string; label: string }[] = [];
  const known = new Set(base.map((o) => o.value));

  const addExtra = (v: string) => {
    const trimmed = v.trim();
    if (!trimmed || known.has(trimmed)) return;
    known.add(trimmed);
    extras.push({ value: trimmed, label: `${trimmed} (aktuell)` });
  };

  if (Array.isArray(currentValue)) {
    currentValue.forEach((v) => addExtra(String(v ?? "")));
  } else {
    addExtra(String(currentValue ?? ""));
  }

  return [...extras, ...base];
}

/** Vergleichswert ist eine Liste: `in`/`notIn` (skalare Quelle) und `containsAny`/`containsAll` (Liste als Quelle). */
export function isMultiValueOperator(operator: string): boolean {
  return operator === "in" || operator === "notIn" || operator === "containsAny" || operator === "containsAll";
}

/**
 * Passt die Form des Vergleichswerts an den Operator an.
 * Ein Einzelwert wird zur Einelement-Liste, eine Liste beim Wechsel auf einen
 * Einzelvergleich auf den ersten Eintrag reduziert. Kommagetrennte Texte werden
 * in einzelne Listeneinträge aufgeteilt.
 */
export function coerceRuleValueForOperator(operator: string, value: unknown): unknown {
  const normalized = normalizeOperator(operator);
  if (isMultiValueOperator(normalized)) {
    if (Array.isArray(value)) {
      return value
        .map((entry) => (typeof entry === "string" ? entry.trim() : entry))
        .filter((entry) => entry != null && String(entry).trim() !== "");
    }
    if (typeof value === "string") {
      return value
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean);
    }
    if (value == null || value === "") return [];
    return [value];
  }
  if (Array.isArray(value)) {
    return value.length ? value[0] : "";
  }
  return value ?? "";
}

const SELECT_OPERATORS: BehaviorRuleOperator[] = ["eq", "neq", "in", "notIn"];
const ARRAY_OPERATORS: BehaviorRuleOperator[] = ["contains", "containsAny", "containsAll"];
const BOOL_OPERATORS: BehaviorRuleOperator[] = ["eq", "neq"];
const NUMBER_OPERATORS: BehaviorRuleOperator[] = ["eq", "neq", "gt", "gte", "lt", "lte"];

/** Operatoren, die zum Quellfeld passen. Ohne Feld bleiben alle Operatoren wählbar. */
export function operatorsForSourceField(sourceField: SourceFieldMeta | null): BehaviorRuleOperator[] {
  if (!sourceField) return [...BEHAVIOR_RULE_OPERATORS];
  if (isBooleanSourceField(sourceField)) return [...BOOL_OPERATORS];
  if (sourceField.type === "multiselect" || sourceField.type === "article_ref_list") {
    return [...ARRAY_OPERATORS];
  }
  if (isOptionSourceField(sourceField)) return [...SELECT_OPERATORS];
  if (sourceField.type === "number" || sourceField.type === "date") return [...NUMBER_OPERATORS];
  return [...SELECT_OPERATORS];
}

const BOOLEAN_SOURCE_TYPES = new Set(["checkbox", "bool", "boolean"]);

/**
 * Leerer Vergleichswert einer Ja/Nein-Quelle.
 * Der Dropdown zeigt dann bereits „true“, ohne dass der Boolean gespeichert wurde.
 */
export function coerceBlankBooleanRuleValue(sourceType: string | null | undefined, value: unknown): unknown {
  const type = String(sourceType || "").trim().toLowerCase();
  if (!BOOLEAN_SOURCE_TYPES.has(type)) return value;
  if (value == null) return true;
  if (typeof value === "string" && value.trim() === "") return true;
  return value;
}

/** Listenförmige Vergleichswerte (`in`, `notIn`, `containsAny`, `containsAll`) vor dem Speichern normalisieren. */
export function normalizeVisibilityRulesForSave(
  rules: unknown,
  sourceTypeFor?: (scope: string, fieldKey: string) => string | null | undefined,
): unknown[] {
  if (!Array.isArray(rules)) return [];
  return rules.map((raw) => {
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return raw;
    const rule = raw as VisibilityRule;
    const scope = normalizeSourceScope(rule.source_scope);
    const fieldKey = String(rule.source_field_key || "").trim();
    const aligned = coerceBlankBooleanRuleValue(sourceTypeFor?.(scope, fieldKey), rule.value);
    if (typeof rule.operator !== "string" || !isMultiValueOperator(normalizeOperator(rule.operator))) {
      if (aligned === rule.value) return raw;
      return { ...rule, value: aligned };
    }
    const operator = normalizeOperator(rule.operator);
    return {
      ...rule,
      operator,
      value: coerceRuleValueForOperator(operator, aligned),
    };
  });
}

function equalityRuleGroupId(rule: VisibilityRule): string | null {
  if (normalizeOperator(rule.operator) !== "eq") return null;
  const key = String(rule.source_field_key || "").trim();
  if (!key) return null;
  return `${normalizeSourceScope(rule.source_scope)}:${key}`;
}

/**
 * Mehrere „ist gleich“-Regeln auf dasselbe Feld werden zu einer „ist einer von“-Regel.
 * Regeln auf andere Felder bleiben stehen und gelten weiterhin zusätzlich.
 */
export function mergeDuplicateEqualityRules(rules: VisibilityRule[]): VisibilityRule[] {
  const counts = new Map<string, number>();
  for (const rule of rules) {
    const id = equalityRuleGroupId(rule);
    if (!id) continue;
    counts.set(id, (counts.get(id) ?? 0) + 1);
  }
  const emitted = new Set<string>();
  const next: VisibilityRule[] = [];
  for (const rule of rules) {
    const id = equalityRuleGroupId(rule);
    if (!id || (counts.get(id) ?? 0) < 2) {
      next.push(rule);
      continue;
    }
    if (emitted.has(id)) continue;
    emitted.add(id);
    const values: unknown[] = [];
    const seen = new Set<string>();
    for (const candidate of rules) {
      if (equalityRuleGroupId(candidate) !== id) continue;
      const entries = Array.isArray(candidate.value) ? candidate.value : [candidate.value];
      for (const entry of entries) {
        const token = String(entry ?? "").trim();
        if (!token || seen.has(token)) continue;
        seen.add(token);
        values.push(typeof entry === "string" ? token : entry);
      }
    }
    next.push(
      normalizeVisibilityRule({
        ...rule,
        operator: "in",
        value: values,
      }),
    );
  }
  return next;
}

/** Aktuellen Operator immer anzeigen, auch wenn er zum Feldtyp nicht mehr passt. */
export function operatorChoicesForSourceField(
  sourceField: SourceFieldMeta | null,
  current: string | undefined,
): BehaviorRuleOperator[] {
  const base = operatorsForSourceField(sourceField);
  const currentOp = normalizeOperator(current);
  if (base.includes(currentOp)) return base;
  return [currentOp, ...base];
}

export function isOptionSourceField(sourceField: SourceFieldMeta | null): boolean {
  if (!sourceField) return false;
  return OPTION_FIELD_TYPES.has(sourceField.type);
}

export function isBooleanSourceField(sourceField: SourceFieldMeta | null): boolean {
  if (!sourceField) return false;
  return sourceField.type === "checkbox" || sourceField.type === "bool";
}
